import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ResponsiveContainer, PieChart as RePieChart, Label, Pie, Cell, Sector } from "recharts";
import { Grid, makeStyles, Typography } from "@material-ui/core";

import { ColorIndicator } from "../colorIndicator";
import { GRAPH_UNITS } from "../../pages/dashboard/constants";
import { nKilowattFormatter, currencyFormatter, nFormatter } from "../../services";

const useStyles = makeStyles((theme) => ({
  responsiveContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  centeredLabel: {
    "& > tspan": {
      fontWeight: "bold",
      fontSize: 24,
    },
  },
  legendRow: {
    marginTop: theme.spacing(0.2),
    marginBottom: theme.spacing(0.2),
  },
  legendContainer: {
    marginLeft: theme.spacing(2.5),
    marginRight: theme.spacing(2.5),
    "& p": {
      fontSize: 12,
    },
  },
  colorIndicatorContainer: {
    display: "flex",
    "& > div": {
      alignSelf: "center",
    },
  },
  centerTextContainer: {
    position: "absolute",
    "& > p": {
      lineHeight: 1,
    },
  },
  centerBottomLabel: {
    fontSize: 21,
    fontWeight: 700,
    lineHeight: 24,
  },
  centerTopLabel: {
    transform: "translateY(10px)",
  },
}));

const renderActiveShape = ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) => {
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 5}
        outerRadius={outerRadius + 5}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    </g>
  );
};

export const PieChart = ({
  height,
  data,
  colors,
  name,
  centeredComponent,
  legends,
  unit,
  legendTitleColWidth,
}) => {
  const classes = useStyles();
  const [activeIndex, setActiveIndex] = useState(null);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  const total = useMemo(() => {
    let t = data.reduce((prev, current) => prev + current.value, 0);
    if (!Number.isInteger(t)) {
      t = +t.toFixed(1);
    }
    return t;
  }, [data]);

  const PieCenteredLabel = ({ value }) => {
    if (!value) return;

    const fontSize = value.length > 7 ? 17 : 21;

    return (
      <text
        x="50%"
        y="50%"
        fontSize={fontSize}
        fontWeight="700"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    );
  };

  // Label in center of the piechart issue
  // https://github.com/recharts/recharts/issues/191
  const centeredLabel = useMemo(() => {
    let displayedValue = total;
    if (activeIndex !== null) {
      displayedValue = data[activeIndex].value;
    }

    switch (name) {
      case "SaveThePlanet":
        return (
          <>
            <Label
              value={nFormatter(displayedValue)}
              className={classes.centerBottomLabel}
              position="centerBottom"
              content={<PieCenteredLabel />}
            />
            <Label value="tCO2" className={classes.centerTopLabel} position="centerTop" />
          </>
        );
      case "CarbonRevenue":
        return (
          <Label
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            className={classes.centerBottomLabel}
            content={<PieCenteredLabel />}
          />
        );
      case "PowerRevenue":
        return (
          <Label
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            className={classes.centerBottomLabel}
            content={<PieCenteredLabel />}
          />
        );
      case "PowerRevenueKWH": {
        let [pTBottomValue, pTTopValue] = nKilowattFormatter(displayedValue).split(" ");

        return (
          <>
            <Label
              value={pTBottomValue}
              className={`${classes.centerBottomLabel}`}
              position="centerBottom"
              content={<PieCenteredLabel />}
            />
            <Label value={pTTopValue} className={classes.centerTopLabel} position="centerTop" />
          </>
        );
      }
      case "ProductionTotal":
        return (
          <Label
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            className={classes.centerBottomLabel}
            content={<PieCenteredLabel />}
          />
        );
      case "ProductionTotalKWH": {
        let pTBottomValue = displayedValue;
        let pTTopValue = null;

        if (displayedValue > 999) {
          if (unit === GRAPH_UNITS.kwh) {
            const formattedValue = nKilowattFormatter(displayedValue);
            [pTBottomValue, pTTopValue] = formattedValue.split(" ");
          } else if (unit !== undefined) {
            pTBottomValue = currencyFormatter(displayedValue, currencySymbol);
          }
        }

        return (
          <>
            <Label
              value={pTBottomValue}
              className={classes.centerBottomLabel}
              position="centerBottom"
              content={<PieCenteredLabel />}
            />
            <Label value={pTTopValue} className={classes.centerTopLabel} position="centerTop" />
          </>
        );
      }
      case "InstalledCapacity":
        return (
          <Label
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            className={classes.centerBottomLabel}
            content={<PieCenteredLabel />}
          />
        );
      case "InstalledCapacityKWH": {
        let [iCBottomValue, iCTopValue] = nKilowattFormatter(displayedValue).split(" ");

        return (
          <>
            <Label
              value={iCBottomValue}
              className={classes.centerBottomLabel}
              position="centerBottom"
              content={<PieCenteredLabel />}
            />
            <Label value={iCTopValue} className={classes.centerTopLabel} position="centerTop" />
          </>
        );
      }
      case "ConnectionStatus": {
        return (
          <>
            {activeIndex !== null ? (
              <>
                <Label
                  value={`${((data[activeIndex].value / total)?.toFixed(2) * 100).toFixed()} %`}
                  className={classes.centerBottomLabel}
                  style={{ transform: "translateY(-5px)" }}
                  position="centerBottom"
                />
                <Label value={data[activeIndex].middleLabel} position="centerTop" />
                <Label
                  value={data[activeIndex].bottomLabel}
                  className={classes.centerTopLabel}
                  position="centerTop"
                />
              </>
            ) : (
              <>
                <Label
                  value={`${((data[0].value / total).toFixed(2) * 100).toFixed()} %`}
                  className={classes.centerBottomLabel}
                  style={{ transform: "translateY(-5px)" }}
                  position="centerBottom"
                />
                <Label value={data[0].middleLabel} position="centerTop" />
                <Label
                  value={data[0].bottomLabel}
                  className={classes.centerTopLabel}
                  position="centerTop"
                />
              </>
            )}
          </>
        );
      }
      case "UnrealizedPowerRevenue":
        return (
          <Label
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            className={classes.centerBottomLabel}
            content={<PieCenteredLabel />}
          />
        );
      default:
        return (
          <Label
            className={classes.centeredLabel}
            value={currencyFormatter(displayedValue, currencySymbol)}
            position="center"
            content={<PieCenteredLabel />}
          />
        );
    }
  }, [
    name,
    total,
    classes.centeredLabel,
    activeIndex,
    data,
    classes.centerTopLabel,
    classes.centerBottomLabel,
    unit,
  ]);

  const renderCustomizedLabel = ({ cx, cy, payload, midAngle, innerRadius, outerRadius, key }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const value = parseInt((payload.value / total).toFixed(2) * 100);

    if (!value) return;

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fontSize: key === data[activeIndex]?.key ? "14px" : "12px" }}
      >
        {`${value}%`}
      </text>
    );
  };

  return (
    <>
      <div className={classes.responsiveContainer}>
        <ResponsiveContainer width="100%" height={height || 200}>
          <RePieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius={50}
              outerRadius={80}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              labelLine={false}
              label={renderCustomizedLabel}
            >
              {!centeredComponent && centeredLabel}
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.key]} stroke={colors[entry.key]} />
              ))}
            </Pie>
          </RePieChart>
        </ResponsiveContainer>

        {/* text inside circle for some special cases */}
        {centeredComponent &&
          (centeredComponent === true ? (
            <div className={classes.centerTextContainer}>
              <Typography variant="h2" align="center">
                {activeIndex === null ? total : data[activeIndex].value}
              </Typography>
              <Typography align="center">
                {activeIndex === null ? unit : data[activeIndex].unit}
              </Typography>
            </div>
          ) : (
            centeredComponent
          ))}
      </div>

      {/* generic legends  */}
      {legends && (
        <div className={classes.legendContainer}>
          {data.map((entry, index) => (
            <Grid className={classes.legendRow} key={index} container justifyContent="center">
              <Grid className={classes.colorIndicatorContainer} item xs={1}>
                <ColorIndicator color={colors[entry.key]} />
              </Grid>
              <Grid item xs={legendTitleColWidth || 7}>
                <Typography>{legends[entry.key]}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography>
                  {!!entry.noUnit
                    ? entry.value
                    : entry.unit
                    ? `${entry.value} ${entry.unit}`
                    : `${currencySymbol}${entry.value}`}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </div>
      )}
    </>
  );
};
